import React from "react";
import { useState, useRef } from "react";
import MainHeader from "./MainHeader";
import "../style/colors.scss";
import MainSideBar from "./MainSideBar";
import MainNavDrawer from "./MainNavDrawer";
import { Outlet } from "react-router";

import "../style/colors.scss";
import "../style/zindexes.scss";
import { useLoginSession } from "../LoginSession";

/**
 * Layout component that wraps content pages to provide the header, sidebar, and nav drawer
 *
 * The content is layed out such that the page is centered irrespective of the drawer width(including animation),
 * but still respects the drawers space when space is limited.
 *
 * By default this component uses flex-basis: auto to auto size the content area, but this only works if the intrinsic
 * width of the page is the the desired base width, and explicitly setting the width of the page directly prevents shrinking,
 * so if you want to customize base width you can use the context provided to child components to explicitly set it.
 *
 * Make sure that if you customize the base width that you set 'width: 100%'.
 *
 * flex-grow is not supported on the content since it would ruin the centering logic, setting it on a child page has no effect
 *
 * USAGE IN PAGE COMPONENTS:
 *   import { useOutletContext } from "react-router";
 *
 *   // page that flexes between 500px and 1200px
 *   const ThePageComponent = () => {
 *     const { setBaseWidth } = useOutletContext();
 *     setBaseWidth('1200px');
 *
 *     return (
 *       <div style={{ minWidth: '500px', width: '100%' }}>
 *       ...
 *   };
 */
function MainHud({
    overlayDrawer = false,
    headerLinks = [],
    transparentDrawer = false,
    style = {},
    drawerStyle = {},
    baseWidth: initialBaseWidth,
}) {
    const [baseWidth, setBaseWidth] = useState(initialBaseWidth || "auto");
    const { userInfo } = useLoginSession(false);

    const [drawerOpen, setDrawerOpen] = useState(userInfo?.single_app_ux ? false : !overlayDrawer); // no drawer if single app ux
    if (userInfo?.single_app_ux && drawerOpen) {
        setDrawerOpen(false);
    }

    const augmentedStyle = {
        background: "var(--colors-neutral-25)",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100vw",
        ...style,
    };

    const mainContentRef = useRef(null);

    function handleWheel(event) {
        // Add a class to your main content div
        if (mainContentRef.current) {
            mainContentRef.current.scrollTop += event.deltaY;
        }
    }

    return (
        <div style={augmentedStyle}>
            <MainHeader
                style={{ zIndex: "var(--z-index-base-very-high)" }}
                onToggleDrawer={() => setDrawerOpen((x) => !x)}
                links={headerLinks}
                drawerDisabled={!!userInfo?.single_app_ux}
            />

            <div
                style={{
                    display: "flex",
                    flexGrow: 1,
                    minHeight: "0", //without this the side bar won't shrink vertically enough since it's the only thing without a scroll bar
                }}
            >
                <MainSideBar style={{ zIndex: "calc(var(--z-index-base-very-high) - 1)" }} />

                {/* wrapper separating everything else from MainSideBar so MainSideBar shrinks last, 
            thats why this doesn't shrink but grows from 0 even though inner stuff can shrink*/}
                <div style={{ display: "flex", flexBasis: 0, flexShrink: 0, flexGrow: 1, maxWidth: "100%" }}>
                    {/* wrapped this with grow only div div so it can balance with other side */}
                    <div style={{ flexBasis: 0, flexShrink: 0, flexGrow: 1, display: "flex" }}>
                        <MainNavDrawer
                            transparent={transparentDrawer}
                            overlay={overlayDrawer}
                            isOpen={drawerOpen}
                            style={{ ...drawerStyle, display: !userInfo || userInfo.single_app_ux ? "none" : "flex" }}
                        />
                        {/* scrollable area that propagates scroll to the right side */}
                        <span
                            onWheel={handleWheel}
                            style={{
                                width: "100%",
                                height: "100%",
                            }}
                        />
                    </div>

                    {/* this div is only here to group content with right side for scrolling purposes(scrollbar to far right) */}
                    <div
                        ref={mainContentRef}
                        style={{ display: "flex", flexBasis: baseWidth, flexGrow: 1, flexShrink: 1, overflowY: "auto" }}
                    >
                        <div
                            style={{
                                flexBasis: baseWidth,
                                maxWidth: "100%",
                                flexShrink: 1,
                                flexGrow: 0, //cannot allow page to grow since it will ruin the centering logic
                            }}
                        >
                            <Outlet context={{ setBaseWidth: setBaseWidth }} />
                        </div>

                        {/* dummy side area for centering, grows along with other side to balance and center content regardless of drawer width */}
                        <div style={{ flexBasis: 0, flexShrink: 0, flexGrow: 1, display: "flex" }}>
                            <span style={{ marginLeft: "auto" }} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MainHud;
